import React, { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip, KV, useModuleTab } from '../../components/ModuleFrame';
import { Spark, useVitals, hostMemory } from '../../components/VitalsSpark';
import { apiGet } from '../../utils/backendApi';
import { useDoc } from '../../data/firestore';

// Economics — the org's unit economics for a month, read from the hourly
// rollup orgs/{org}/economics/{YYYY-MM} (api/economicsWorker.js) through
// GET /apps/:orgId/economics (admins; `fleet` only reaches operators).
//
// Revenue is shown on two bases and the labels say which: "theoretical" is
// list price for every live app and the org's platform plan + metered
// overage (what the month would bill if everyone paid list); "actual" is the
// Stripe-backed statement when one exists. COGS is the cost rollup (infra,
// model, Dev Console, platform) plus the apportioned plane overhead. Margin
// is computed on both. Nothing here is a bill — Settings › Costs is.

const usd = (v, d = 2) => (v == null || !Number.isFinite(Number(v)) ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`);
const pct = (v) => (v == null ? '—' : `${Math.round(v)}%`);
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());
const tokens = (v) => (v == null ? '—' : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : String(v));

const BASIS_TONE = { actual: 'ok', theoretical: 'warn', none: 'muted', estimated: 'muted', 'rollup+overhead': 'muted' };
function Basis({ basis, note }) {
  if (!basis) return null;
  return <Chip tone={BASIS_TONE[basis] || 'muted'} title={note || undefined}>{basis}</Chip>;
}

// Margin coloured by sign: negative is a problem, not a style.
function Margin({ m }) {
  if (!m) return <span>—</span>;
  const neg = (m.usd ?? 0) < 0;
  return (
    <span style={{ color: neg ? 'var(--danger)' : 'inherit', fontWeight: neg ? 600 : 400 }} title={m.pct == null ? 'no revenue on this basis' : undefined}>
      {usd(m.usd)}{m.pct != null && <span className="cs-help" style={{ marginLeft: 4 }}>{pct(m.pct)}</span>}
    </span>
  );
}

const periodKey = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
const shiftPeriod = (p, n) => { const [y, m] = p.split('-').map(Number); return periodKey(new Date(Date.UTC(y, m - 1 + n, 1))); };

function Stat({ label, value, sub, tone }) {
  return (
    <div className="mf-stat">
      <div className="mf-stat-k">{label}</div>
      <div className="mf-stat-v" style={tone === 'danger' ? { color: 'var(--danger)' } : undefined}>{value}</div>
      {sub && <div className="cs-help">{sub}</div>}
    </div>
  );
}

// Fleet routing learning loop (api/fleetRoutingWorker.js, operators only):
// what every instance's Dev Console routing and the plane's hosted model
// seam actually did, where the money went per tier, which engine was right
// when they disagreed, misroutes, policy replay, recommendations, and the
// learned router's gate. Everything here is read-only advice: the runbook
// operate-smart-routing applies a change by hand.
const TIER_ORDER = ['bulk', 'strong', 'frontier'];
function RoutingTab({ data, period }) {
  if (!data) return <p className="mf-muted">Loading routing insights…</p>;
  if (data.error) return <p className="cs-form-error" role="alert">{data.error}</p>;
  const ins = data.insights;
  if (!ins) return <p className="mf-muted">No routing insights yet — the fleet routing worker writes the first doc on its daily pass once instances have phoned routing samples home.</p>;
  const dev = ins.dev; const hosted = ins.hosted; const learned = data.learned || null;
  const engines = dev ? Object.entries(dev.engines || {}).filter(([, e]) => e.compared) : [];
  return (
    <>
      {ins.period !== period && <p className="cs-help">Showing {ins.period} (latest) — no insights doc for {period}.</p>}
      <div className="mf-stats" data-nova-note="Fleet routing headline: how many instances report, dev sessions and hosted calls in the window, how many recommendations, and whether the learned router passed its gate.">
        <Stat label="Instances reporting" value={num(ins.instances)} sub={`${ins.windowDays} day window · computed ${new Date(ins.at).toLocaleString()}`} />
        <Stat label="Dev sessions" value={num(dev?.sessions)} sub={dev ? `${num(dev.labelled)} outcome-labelled · ${num(dev.pinned)} pinned` : 'none yet'} />
        <Stat label="Hosted calls" value={num(hosted?.calls)} sub={hosted ? `${usd(hosted.usd)} · ${hosted.escalationPct ?? 0}% escalated · ${hosted.byoPct ?? 0}% BYO` : `last ${ins.hostedWindowDays} days`} />
        <Stat label="Recommendations" value={num(ins.recommendations?.length || 0)} sub="advice, never auto-applied" />
        <Stat label="Learned router" value={learned?.status === 'ready' ? 'ready · shadow' : 'draft'} sub={learned?.gate?.ok ? `${learned.rules?.length} rules · ${learned.gate.learnedAcc}% vs rules ${learned.gate.rulesAcc}%` : (learned?.gate?.reason || 'no corpus yet')} tone={learned?.status === 'ready' ? undefined : undefined} />
      </div>

      <ModuleFrame.Columns cols={2}>
        <ModuleFrame.Panel title="Dev Console tiers (fleet)" meta={dev ? `${num(dev.routed)} routed sessions` : '—'}>
          {dev ? (
            <table className="mf-table">
              <thead><tr><th>Tier</th><th>Sessions</th><th>Accepted</th><th>Failed</th><th>Spend</th><th>$/accepted</th></tr></thead>
              <tbody>
                {TIER_ORDER.map((k) => { const v = dev.byTier?.[k]; return v ? <tr key={k}><td>{k}</td><td>{num(v.sessions)}</td><td>{pct(v.acceptedPct)}</td><td style={(v.failedPct || 0) >= 25 ? { color: 'var(--danger)' } : undefined}>{pct(v.failedPct)}</td><td>{usd(v.usd)}</td><td>{usd(v.usdPerAccepted)}</td></tr> : null; })}
              </tbody>
            </table>
          ) : <p className="cs-help">No instance has reported routing samples yet.</p>}
          {engines.length > 0 && (
            <>
              <h4 style={{ margin: '12px 0 6px' }}>Engines vs outcome</h4>
              <ul className="mf-list">
                {engines.map(([k, e]) => (
                  <li key={k} className="mf-row"><span className="mf-row-grow">{k} · agreement {pct(e.agreementPct)} on {num(e.compared)}</span><span title="on disagreements with a known outcome, how often this engine's verdict matched the outcome">{e.otherRightPct != null ? `right ${pct(e.otherRightPct)} (${e.other_wins}:${e.applied_wins})` : '—'}</span></li>
                ))}
              </ul>
            </>
          )}
          {dev && (dev.misroutes?.under?.length || dev.misroutes?.over?.length) ? (
            <>
              <h4 style={{ margin: '12px 0 6px' }}>Misroutes</h4>
              <ul className="mf-list">
                {(dev.misroutes.under || []).slice(0, 5).map((u) => <li key={`u-${u.bucket}-${u.tier}`} className="mf-row"><Chip tone="warn">under</Chip><span className="mf-row-grow" style={{ marginLeft: 6 }}>{u.bucket} on {u.tier}</span><span>{pct(u.failedPct)} failed of {u.settled}</span></li>)}
                {(dev.misroutes.over || []).slice(0, 5).map((o) => <li key={`o-${o.bucket}-${o.tier}`} className="mf-row"><Chip tone="muted">over</Chip><span className="mf-row-grow" style={{ marginLeft: 6 }}>{o.bucket} on {o.tier}</span><span>{pct(o.acceptedPct)} accepted · {usd(o.usd)}</span></li>)}
              </ul>
            </>
          ) : null}
        </ModuleFrame.Panel>

        <ModuleFrame.Panel title="Hosted model seam" meta={hosted ? `${usd(hosted.usd)} · ${tokens(hosted.tokens)} tokens · ${hosted.up}↑ ${hosted.down}↓` : '—'}>
          {hosted ? (
            <>
              <table className="mf-table">
                <thead><tr><th>Route · stage</th><th>Tier</th><th>Model</th><th>Calls</th><th>Spend</th><th>Esc</th><th>p95</th><th>👎</th></tr></thead>
                <tbody>
                  {(hosted.rows || []).slice(0, 12).map((r) => (
                    <tr key={`${r.route}-${r.stage}-${r.tier}-${r.model}`}><td>{r.route}{r.stage ? ` · ${r.stage}` : ''}</td><td>{r.tier}</td><td className="cs-help">{r.model}</td><td>{num(r.calls)}</td><td>{usd(r.usd)}</td><td>{pct(r.escalationPct)}</td><td>{r.p95Ms != null ? `${num(r.p95Ms)} ms` : '—'}</td><td>{r.thumbsDownPct != null ? pct(r.thumbsDownPct) : '—'}</td></tr>
                  ))}
                </tbody>
              </table>
              {hosted.replay && (
                <KV rows={[
                  ['Actual spend', usd(hosted.replay.live?.actualUsd)],
                  ['Replay: today\'s policy', `${usd(hosted.replay.live?.projectedUsd)} (${hosted.replay.live?.moved || 0} calls would move)`],
                  ['Replay: everything on bulk', `${usd(hosted.replay.allBulk?.projectedUsd)} · ${hosted.replay.allBulk?.savingsPct ?? '—'}% floor`],
                ]} />
              )}
            </>
          ) : <p className="cs-help">No hosted call samples in the window (models.samples.enabled).</p>}
        </ModuleFrame.Panel>
      </ModuleFrame.Columns>

      <ModuleFrame.Panel title="Recommendations" meta="advice for operate-smart-routing — nothing is applied automatically">
        {ins.recommendations?.length ? (
          <ul className="mf-list" data-nova-note="Each recommendation names the scope (an instance's dev.router tier/ceiling or a plane models.policy route), the evidence and the proposed change; an operator applies it by hand per the operate-smart-routing runbook.">
            {ins.recommendations.map((r, i) => (
              <li key={i} className="mf-row" style={{ alignItems: 'flex-start' }}>
                <Chip tone={r.confidence === 'high' ? 'ok' : 'muted'}>{r.kind}</Chip>
                <span className="mf-row-grow" style={{ marginLeft: 8 }}><strong>{r.scope}</strong> — {r.evidence}. <span className="cs-help">Proposed: {r.proposed} (n={r.sampleSize})</span></span>
              </li>
            ))}
          </ul>
        ) : <p className="cs-help">Nothing to recommend from this window — tiers are earning their keep.</p>}
      </ModuleFrame.Panel>

      <ModuleFrame.Panel title="Learned router" meta={learned ? `${learned.status} · ${learned.rules?.length || 0} rules · ${learned.blocks} blocks` : 'not induced yet'}>
        {learned ? (
          <>
            <p className="cs-help">Decision list induced from {num(learned.gate?.corpus)} outcome-labelled sessions: learned {learned.gate?.learnedAcc ?? '—'}% vs rules {learned.gate?.rulesAcc ?? '—'}% · {learned.gate?.ok ? 'gate passed — pipeline router-learned is ready and rides the app bundle to every managed instance, where it runs in shadow beside the live engine' : `gate not passed: ${learned.gate?.reason}`}. Shadow agreement per instance lands in its devRouting ledger (shadowAgree / shadowDisagree) and in the samples above.</p>
            <ul className="mf-list">
              {(learned.rules || []).slice(0, 10).map((r, i) => <li key={i} className="mf-row"><span className="mf-row-grow">on rules {r.base}: {r.bucket} → <strong>{r.target}</strong></span><span className="cs-help">{Math.round((r.purity || 0) * 100)}% of {r.support}</span></li>)}
            </ul>
          </>
        ) : <p className="cs-help">The worker induces the learned router once the labelled corpus reaches apps.fleetRouting.minCorpus.</p>}
      </ModuleFrame.Panel>
    </>
  );
}

export default function EconomicsModule() {
  const { org, myRole } = useOutletContext();
  // The Blueprint's portfolio rollup (api/blueprintWorker.js) — savings the
  // org's initiatives forecast and have realized, beside the month's margin.
  const { doc: blueprint } = useDoc(`orgs/${org.id}/blueprint/summary`);
  const isAdmin = ['admin', 'owner'].includes(myRole);
  const [tab, setTab] = useModuleTab('margin');
  const [period, setPeriod] = useState(periodKey(new Date()));
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    setLoading(true); setErr('');
    apiGet(`/apps/${org.id}/economics?period=${period}`)
      .then((r) => { if (alive) setData(r.economics); })
      .catch((e) => { if (alive) setErr(e.message || 'failed to load'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [org.id, period, isAdmin]);

  const ec = data;
  const fleet = ec?.fleet || null;
  const t = ec?.totals;
  const apps = ec?.apps || [];
  const fleetVitals = useVitals(fleet && tab === 'fleet' ? '/apps/admin/vitals' : null);
  // Fleet routing insights (api/fleetRoutingWorker.js) — operators only,
  // loaded when the tab opens; `latest` when the month has no doc yet.
  const [routing, setRouting] = useState(null);
  useEffect(() => {
    if (!fleet || tab !== 'routing') return undefined;
    let alive = true;
    apiGet(`/apps/admin/routing-insights?period=${period}`)
      .then((r) => { if (!alive) return; if (r.insights) setRouting(r); else return apiGet('/apps/admin/routing-insights').then((l) => { if (alive) setRouting(l); }); })
      .catch((e) => { if (alive) setRouting({ error: e.message || 'failed to load' }); });
    return () => { alive = false; };
  }, [fleet, tab, period]);

  const tabs = useMemo(() => [
    ...(fleet ? [{ id: 'fleet', label: 'Fleet' }, { id: 'routing', label: 'Routing' }] : []),
    { id: 'margin', label: 'Margin' },
    { id: 'revenue', label: 'Revenue' },
    { id: 'costs', label: 'Costs', count: apps.length || undefined },
    { id: 'tokens', label: 'Tokens' },
  ], [fleet, apps.length]);

  if (!isAdmin) {
    return (
      <ModuleFrame title="Economics" subtitle="Revenue · Costs · Margin · Tokens" accent="#4ade80" help="Admins and owners only.">
        <p className="mf-muted">Ask an admin of this organization for the economics view.</p>
      </ModuleFrame>
    );
  }

  const periodNav = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setPeriod(shiftPeriod(period, -1))} aria-label="previous month">‹</button>
      <strong>{period}</strong>
      <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={period >= periodKey(new Date())} onClick={() => setPeriod(shiftPeriod(period, 1))} aria-label="next month">›</button>
    </span>
  );

  return (
    <ModuleFrame
      title="Economics"
      subtitle="Revenue · Costs · Margin · Tokens"
      accent="#4ade80"
      note={`Economics, tab ${tab}${ec ? `, rollup from ${ec.updatedAt ? new Date(ec.updatedAt).toLocaleString() : 'unknown time'}` : ', no rollup for this period yet'}${fleet ? ', operator fleet + routing views available' : ''}. Read-only: NoVA explains the numbers (list_orgEconomics, list_orgCost) and the basis chips; billing changes happen in Settings. The Routing tab is the fleet routing learning loop (knowledge doc fleet-routing-insights): recommendations are advice for the operate-smart-routing runbook, the learned router runs in shadow on instances until an operator flips dev.router.engine there.`}
      help="What the month would bill at list price (theoretical), what Stripe actually billed (actual), what it cost us to serve (cost rollup + apportioned plane overhead), and the margin on both. Rebuilt hourly; the basis chip on every number says where it came from."
      context={(
        <>
          {periodNav}
          {ec?.updatedAt && <Chip tone="muted" title={new Date(ec.updatedAt).toLocaleString()}>rolled {Math.round((Date.now() - ec.updatedAt) / 60000)} min ago</Chip>}
          {fleet && <Chip tone="ok">operator · fleet</Chip>}
        </>
      )}
      tabs={tabs}
      tab={tabs.some((x) => x.id === tab) ? tab : 'margin'}
      onTab={setTab}
      verbs={[
        { label: 'Costs & limits', hint: 'Settings › Costs', to: `/orgs/${org.id}/settings`, primary: true },
        { label: 'Models & credits', hint: 'tier ladder, credits', to: `/orgs/${org.id}/administration?tab=models` },
        { label: 'Instances', hint: 'health, services', to: `/orgs/${org.id}/deployments?tab=instances` },
      ]}
    >
      {err && <p className="cs-form-error" role="alert">{err}</p>}
      {loading && !ec && <p className="mf-muted">Loading…</p>}
      {!loading && !ec && !err && <p className="mf-muted">No economics rollup for {period} yet — the worker writes one every hour once there is a cost rollup for the month.</p>}

      {ec && tab === 'margin' && (
        <>
          <ModuleFrame.Panel title="This month" meta={`${apps.length} app rows · basis ${ec.basis?.revenue || 'mixed'}`}>
            <div className="mf-stats">
              <Stat label="Revenue (theoretical)" value={usd(t.revenueTheoreticalUsd)} sub="list price · live apps + platform plan + overage" />
              <Stat label="Revenue (actual)" value={usd(t.revenueActualUsd)} sub="Stripe-backed statements" />
              <Stat label="COGS" value={usd(t.cogsUsd)} sub={`infra ${usd(t.infraUsd)} · model ${usd(t.modelUsd)} · dev ${usd(t.devUsd)} · overhead ${usd(t.overheadUsd)}`} />
              <Stat label="Margin (theoretical)" value={<Margin m={t.margin} />} />
              <Stat label="Margin (actual)" value={<Margin m={t.marginActual} />} tone={(t.marginActual?.usd ?? 0) < 0 ? 'danger' : undefined} sub={t.marginActual?.pct == null ? 'no paid revenue this month' : undefined} />
              {blueprint && (
                <Stat label="Blueprint savings" value={`${usd(blueprint.months?.[period]?.realizedUsd ?? 0, 0)} realized`} sub={<Link to={`/orgs/${org.id}/blueprint?tab=impact`}>{usd(blueprint.forecastAnnualUsd, 0)}/yr forecast · {blueprint.active || 0} active initiatives</Link>} />
              )}
            </div>
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Margin by app" meta="sorted worst first · negative rows are the ones to look at">
            <table className="mf-table">
              <thead><tr><th>App</th><th>Plan</th><th>Revenue</th><th>Basis</th><th>COGS</th><th>Margin</th><th>Margin (actual)</th></tr></thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.appId}>
                    <td><Link to={`/orgs/${org.id}/apps/${a.appId}`}>{a.name || a.slug}</Link><div className="cs-help">{a.slug}{a.pool ? ' · pool' : ''}{a.delivery === 'external' ? ' · external' : ''}{a.status !== 'live' ? ` · ${a.status}` : ''}</div></td>
                    <td>{a.plan || '—'}{a.billing === 'internal' && <Chip tone="muted">internal</Chip>}</td>
                    <td>{usd(a.revenue?.theoreticalUsd)}</td>
                    <td><Basis basis={a.revenue?.basis} note={a.revenue?.note} /></td>
                    <td>{usd(a.cogs?.usd)}</td>
                    <td><Margin m={a.margin} /></td>
                    <td><Margin m={a.marginActual} /></td>
                  </tr>
                ))}
                {ec.platform && (
                  <tr>
                    <td><strong>Shared plane</strong><div className="cs-help">platform plan + metered usage on nventr.io</div></td>
                    <td>{ec.platform.revenue?.planKey || '—'}</td>
                    <td>{usd(ec.platform.revenue?.theoreticalUsd)}</td>
                    <td><Basis basis={ec.platform.revenue?.basis} /></td>
                    <td>{usd(ec.platform.cogsUsd)}</td>
                    <td><Margin m={ec.platform.margin} /></td>
                    <td>—</td>
                  </tr>
                )}
              </tbody>
            </table>
          </ModuleFrame.Panel>
        </>
      )}

      {ec && tab === 'revenue' && (
        <ModuleFrame.Columns cols={2}>
          <ModuleFrame.Panel title="Hosted instances" meta="base fee + metered overage at list price">
            <table className="mf-table">
              <thead><tr><th>App</th><th>Base</th><th>Metered</th><th>Theoretical</th><th>Actual</th><th>Basis</th></tr></thead>
              <tbody>
                {apps.filter((a) => a.revenue).map((a) => (
                  <tr key={a.appId}>
                    <td>{a.name || a.slug}<div className="cs-help">{a.plan || '—'}</div></td>
                    <td>{usd(a.revenue.baseFeeUsd)}</td>
                    <td>{usd(a.revenue.meteredUsd)}</td>
                    <td>{usd(a.revenue.theoreticalUsd)}</td>
                    <td>{usd(a.revenue.actualUsd)}</td>
                    <td><Basis basis={a.revenue.basis} note={a.revenue.note} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="cs-help" style={{ marginTop: 8 }}>“theoretical” = no paid statement this month, priced at list. “actual” = a Stripe statement exists. Pool stacks and internal apps are included and flagged — they are capacity, not customers.</p>
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Platform plan" meta={ec.platform?.revenue?.planKey ? `plan ${ec.platform.revenue.planKey}` : 'no platform plan'}>
            {ec.platform?.revenue ? (
              <>
                <KV rows={[['Subscription', usd(ec.platform.revenue.subscriptionUsd)], ['Metered overage', usd(ec.platform.revenue.overageUsd)], ['Theoretical', usd(ec.platform.revenue.theoreticalUsd)], ['Actual', usd(ec.platform.revenue.actualUsd)]]} />
                <table className="mf-table" style={{ marginTop: 10 }}>
                  <thead><tr><th>Line</th><th>Qty</th><th>Included</th><th>Amount</th></tr></thead>
                  <tbody>
                    {(ec.platform.revenue.lines || []).map((l) => (
                      <tr key={l.key}><td>{l.label}</td><td>{num(l.qty)} {l.unit}</td><td>{num(l.includedQty)}</td><td>{usd((l.amountCents || 0) / 100)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : <p className="mf-muted">—</p>}
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {ec && tab === 'costs' && (
        <ModuleFrame.Panel title="Cost to serve, by app" meta="cost rollup (hourly) + apportioned plane overhead">
          <table className="mf-table">
            <thead><tr><th>App</th><th>Host</th><th>Infra</th><th>Model</th><th>Dev Console</th><th>Platform</th><th>Overhead</th><th>Total</th></tr></thead>
            <tbody>
              {[...apps].sort((a, b) => (b.cogs?.usd || 0) - (a.cogs?.usd || 0)).map((a) => (
                <tr key={a.appId}>
                  <td>{a.name || a.slug}<div className="cs-help">{a.slug}</div></td>
                  <td className="cs-help">{a.host ? a.host.slice(-8) : a.delivery === 'external' ? 'their account' : '—'}</td>
                  <td>{usd(a.cogs?.infraUsd)}</td>
                  <td>{usd(a.cogs?.modelUsd)}</td>
                  <td>{usd(a.cogs?.devUsd)}</td>
                  <td>{usd(a.cogs?.platformUsd)}</td>
                  <td>{usd(a.cogs?.overheadUsd)}</td>
                  <td><strong>{usd(a.cogs?.usd)}</strong></td>
                </tr>
              ))}
              <tr>
                <td><strong>Total</strong></td><td />
                <td>{usd(t.infraUsd)}</td><td>{usd(t.modelUsd)}</td><td>{usd(t.devUsd)}</td><td>{usd(t.platformUsd)}</td><td>{usd(t.overheadUsd)}</td><td><strong>{usd(t.cogsUsd)}</strong></td>
              </tr>
            </tbody>
          </table>
          <p className="cs-help" style={{ marginTop: 8 }}>Infra = this app's share of its host (memory-weighted) + its own AWS Cost Explorer lines. Model = hosted-model spend on the org's ledger and the gateway. Dev Console = Cursor cents the instance reported. Overhead = the plane's bill that no app line claimed, split evenly over live managed apps. External apps carry no infra: their AWS account pays.</p>
        </ModuleFrame.Panel>
      )}

      {ec && tab === 'tokens' && (
        <ModuleFrame.Columns cols={2}>
          <ModuleFrame.Panel title="Hosted model tokens" meta={`${usd(ec.tokens?.hosted?.usd)} · by tier`}>
            <table className="mf-table">
              <thead><tr><th>Tier</th><th>Calls</th><th>Tokens</th><th>Spend</th><th>Share</th></tr></thead>
              <tbody>
                {Object.entries(ec.tokens?.hosted?.byTier || {}).map(([k, v]) => (
                  <tr key={k}><td>{k}</td><td>{num(v.calls)}</td><td>{tokens(v.tokens)}</td><td>{usd(v.usd)}</td><td>{pct(v.sharePct)}</td></tr>
                ))}
              </tbody>
            </table>
            {ec.tokens?.hosted?.byRoute && (
              <>
                <h4 style={{ margin: '12px 0 6px' }}>By route</h4>
                <ul className="mf-list">
                  {Object.entries(ec.tokens.hosted.byRoute).sort((a, b) => (b[1].usd || 0) - (a[1].usd || 0)).map(([k, v]) => <li key={k} className="mf-row"><span className="mf-row-grow">{k}</span><span>{usd(v.usd)}</span></li>)}
                </ul>
              </>
            )}
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Instances" meta="kit counters + Dev Console routing">
            <KV rows={[
              ['Tokens (all)', tokens(ec.tokens?.kit?.tokens)],
              ['Build / run wallets', `${tokens(ec.tokens?.kit?.buildTokens)} / ${tokens(ec.tokens?.kit?.runTokens)}`],
              ['Agent runs', num(ec.tokens?.kit?.agentRuns)],
              ['Dev Console spend', usd(ec.tokens?.kit?.devUsd)],
            ]} />
            {ec.tokens?.routing ? (
              <>
                <h4 style={{ margin: '12px 0 6px' }}>Smart routing (Dev Console)</h4>
                <table className="mf-table">
                  <thead><tr><th>Tier</th><th>Sessions</th><th>Spend</th><th>$/session</th><th>Share</th></tr></thead>
                  <tbody>
                    {Object.entries(ec.tokens.routing.tiers || {}).map(([k, v]) => (
                      <tr key={k}><td>{k}</td><td>{num(v.sessions)}</td><td>{usd(v.usd)}</td><td>{usd(v.usdPerSession)}</td><td>{pct(v.sessionSharePct)} · {pct(v.usdSharePct)} $</td></tr>
                    ))}
                  </tbody>
                </table>
                <p className="cs-help" style={{ marginTop: 6 }}>
                  {num(ec.tokens.routing.sessions)} sessions · {num(ec.tokens.routing.escalations)} escalations · {num(ec.tokens.routing.capped)} capped
                  {ec.tokens.routing.jev && ec.tokens.routing.jev.agreementPct != null && <> · Jev agreement {pct(ec.tokens.routing.jev.agreementPct)} ({usd(ec.tokens.routing.jev.decisionUsd)} on decisions)</>}
                </p>
                {ec.tokens.routing.modes && Object.keys(ec.tokens.routing.modes).length > 0 && (
                  <ul className="mf-list">{Object.entries(ec.tokens.routing.modes).map(([k, v]) => <li key={k} className="mf-row"><span className="mf-row-grow">mode {k}</span><span>{num(v)}</span></li>)}</ul>
                )}
              </>
            ) : <p className="cs-help" style={{ marginTop: 10 }}>No Dev Console routing ledger yet — instances on the routing kit report it hourly.</p>}
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {fleet && tab === 'routing' && <RoutingTab data={routing} period={period} />}

      {ec && fleet && tab === 'fleet' && (
        <>
          <ModuleFrame.Panel title="Fleet" meta={`${fleet.orgs.filter((o) => o.apps).length} orgs with apps · ${fleet.capacity?.used}/${fleet.capacity?.slots} slots · ${fleet.capacity?.liveManaged} live managed`}>
            <div className="mf-stats">
              <Stat label="Revenue (theoretical)" value={usd(fleet.totals.revenueTheoreticalUsd)} />
              <Stat label="Revenue (actual)" value={usd(fleet.totals.revenueActualUsd)} />
              <Stat label="COGS" value={usd(fleet.totals.cogsUsd)} sub={`plane bill ${usd(fleet.totals.planeUsd)} · attributed ${usd(fleet.totals.attributedUsd)} · overhead ${usd(fleet.totals.overheadUsd)}`} />
              <Stat label="Margin (theoretical)" value={<Margin m={fleet.totals.margin} />} />
              <Stat label="Margin (actual)" value={<Margin m={fleet.totals.marginActual} />} tone={(fleet.totals.marginActual?.usd ?? 0) < 0 ? 'danger' : undefined} />
              <Stat label="Hosted tokens" value={usd(fleet.tokens?.hosted?.usd)} sub="whole plane ledger" />
            </div>
          </ModuleFrame.Panel>
          <ModuleFrame.Columns cols={2}>
            <ModuleFrame.Panel title="By organization" meta="sorted by margin">
              <table className="mf-table">
                <thead><tr><th>Org</th><th>Apps</th><th>Revenue</th><th>COGS</th><th>Margin</th><th>Actual</th></tr></thead>
                <tbody>
                  {[...fleet.orgs].filter((o) => o.apps || o.revenueTheoreticalUsd || o.cogsUsd).sort((a, b) => (a.margin?.usd || 0) - (b.margin?.usd || 0)).map((o) => (
                    <tr key={o.orgId}>
                      <td><Link to={`/orgs/${o.orgId}/economics`}>{o.name}</Link>{o.billing === 'internal' && <Chip tone="muted">internal</Chip>}</td>
                      <td>{o.apps}</td><td>{usd(o.revenueTheoreticalUsd)}</td><td>{usd(o.cogsUsd)}</td><td><Margin m={o.margin} /></td><td><Margin m={o.marginActual} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ModuleFrame.Panel>
            <ModuleFrame.Panel title="Hosts" meta="EC2 compute split by memory share">
              <table className="mf-table">
                <thead><tr><th>Host</th><th>Role</th><th>Slots</th><th>Memory</th><th>$/month</th><th>$/slot</th></tr></thead>
                <tbody>
                  {(fleet.hosts || []).map((h) => (
                    <tr key={h.hostId}>
                      <td><code>{h.hostId}</code><div className="cs-help">{h.state}</div></td>
                      <td>{h.role}</td>
                      <td>{h.count}/{h.capacity}</td>
                      <td><Spark points={hostMemory(fleetVitals?.hosts?.[h.hostId]?.rows)} max={100} warnAt={fleetVitals?.thresholds?.hostMemWarnPct ?? 85} unit="%" title="host memory, last 24 h" /> <span className="cs-help">{pct(h.memUsedPct)}</span></td>
                      <td>{usd(h.monthlyUsd)}</td>
                      <td>{usd(h.usdPerSlot)}<div className="cs-help">{usd(h.usdPerCapacitySlot)} at capacity</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="cs-help" style={{ marginTop: 6 }}>{fleet.hosts?.[0]?.basis}</p>
            </ModuleFrame.Panel>
          </ModuleFrame.Columns>
          <ModuleFrame.Columns cols={3}>
            <ModuleFrame.Panel title="Planes" meta="AWS bill per control plane">
              <ul className="mf-list">
                {Object.entries(fleet.planes || {}).map(([slug, p]) => <li key={slug} className="mf-row"><span className="mf-row-grow">{slug}</span><span>{usd(p.totalUsd)}</span></li>)}
              </ul>
            </ModuleFrame.Panel>
            <ModuleFrame.Panel title="Reliability (30 d)" meta={fleet.health?.incidents30d ? `${fleet.health.incidents30d.total} incidents · ${fleet.health.incidents30d.open} open · MTTR p50 ${fleet.health.incidents30d.mttrP50Min} min` : '—'}>
              <ul className="mf-list">
                {Object.entries(fleet.health?.incidents30d?.byKind || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => <li key={k} className="mf-row"><span className="mf-row-grow">{k}</span><span>{v}</span></li>)}
              </ul>
              <KV rows={[
                ['Journeys', fleet.health?.journeys ? `${fleet.health.journeys.totals?.passed}/${fleet.health.journeys.totals?.total} passed${fleet.health.journeys.ok ? '' : ' · failing'}` : '—'],
                ['Self-tests', fleet.health?.selfTest ? `${fleet.health.selfTest.passing}/${fleet.health.selfTest.reporting} instances passing` : '—'],
                ['Kit refs', Object.entries(fleet.health?.kitRefs || {}).map(([k, v]) => `${k} ×${v}`).join(', ') || '—'],
              ]} />
            </ModuleFrame.Panel>
            <ModuleFrame.Panel title="Uptime (30 d)" meta="probe pass rate · worst first">
              <ul className="mf-list">
                {[...(fleet.health?.uptime30d || [])].sort((a, b) => a.pct - b.pct).slice(0, 10).map((u) => (
                  <li key={u.slug} className="mf-row">
                    <span className="mf-row-grow">{u.slug}{u.external && <span className="cs-help"> · external</span>}</span>
                    <span style={{ color: u.pct < 99 ? 'var(--danger)' : 'inherit' }}>{u.pct.toFixed(2)}%</span>
                  </li>
                ))}
              </ul>
            </ModuleFrame.Panel>
          </ModuleFrame.Columns>
        </>
      )}
    </ModuleFrame>
  );
}
